import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/auth";
import axios from "axios";
import toast from "react-hot-toast";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [auth, setAuth] = useAuth();
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await axios.post("/api/v1/auth/login", {
        email,
        password,
      });
      if (res && res.data.success) {
        toast.success(res.data.message);

        // Set auth context
        setAuth({
          ...auth,
          user: res.data.user,
          token: res.data.token,
        });

        // Store in localStorage
        localStorage.setItem("auth", JSON.stringify(res.data));

        // Navigate to the intended page
        const redirectPath = location.state || "/";
        navigate(redirectPath);

        // Refresh the page after navigation
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative overflow-hidden">
      {/* LEFT SIDE - BLACK WITH CURVED EDGE */}
      <div className="w-full lg:w-1/2 bg-black relative flex items-center justify-center min-h-[40vh] lg:min-h-screen py-12 lg:py-0">
        {/* Logo Section */}
        <div className="text-white z-10 text-center px-6">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "'TAN PEARL', serif" }}
          >
            Russh Berry
          </h1>
          <p className="text-base sm:text-lg lg:text-xl mt-3 lg:mt-4 text-gray-400">
            Where Luxury Meets Elegance
          </p>
        </div>

        {/* Curved SVG Divider - Desktop Only */}
        <div className="hidden lg:block absolute right-0 top-0 h-full w-64 overflow-hidden">
          <svg
            className="absolute right-0 h-full"
            viewBox="0 0 400 800"
            preserveAspectRatio="none"
            style={{ transform: "translateX(50%)" }}
          >
            <path d="M 0 0 Q 200 400 0 800 L 400 800 L 400 0 Z" fill="white" />
          </svg>
        </div>

        {/* Mobile/Tablet Wave Divider */}
        <div className="lg:hidden absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg
            className="relative block w-full h-12 sm:h-16"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 C300,100 900,100 1200,0 L1200,120 L0,120 Z"
              fill="white"
            />
          </svg>
        </div>
      </div>

      {/* RIGHT SIDE - WHITE WITH FORM */}
      <div className="w-full lg:w-1/2 bg-white flex items-center justify-center px-6 sm:px-12 lg:px-16 py-12 lg:py-0">
        <div className="w-full max-w-md">
          {/* Sign In Header */}
          <h2 className="text-2xl sm:text-3xl lg:text-3xl font-bold mb-8 lg:mb-12 text-gray-900">
            SIGN IN
          </h2>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {/* Email Input */}
            <div className="relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                className="w-full pl-8 pr-4 py-3 sm:py-3.5 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent text-base placeholder:text-gray-400"
              />
            </div>

            {/* Password Input */}
            <div className="relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="w-full pl-8 pr-4 py-3 sm:py-3.5 border-b-2 border-gray-300 focus:border-black outline-none transition-colors bg-transparent text-base placeholder:text-gray-400"
              />
            </div>

            {/* Forgot Password */}
            <div className="text-left pt-2">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm sm:text-base text-gray-600 hover:text-black transition-colors underline"
              >
                Forgot password?
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-3 sm:py-3 rounded-none font-semibold text-base sm:text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-4"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  LOGGING IN...
                </span>
              ) : (
                "LOGIN"
              )}
            </button>

            {/* OR Divider */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">OR</span>
              </div>
            </div>

            {/* Register Button */}
            <button
              type="button"
              onClick={() => navigate("/register")}
              className="w-full bg-white border-2 border-black text-black py-3 sm:py-3 rounded-none font-semibold text-base sm:text-lg hover:bg-gray-50 transition-colors"
            >
              CREATE ACCOUNT
            </button>
          </form>

          <p className="text-xs sm:text-sm text-gray-500 text-center mt-4 lg:mt-6 leading-relaxed px-2">
            SECURED BY WEBZARK TECHNOLOGIES
          </p>

          {/* Mobile-only Helper Text */}
          <p className="text-center text-xs sm:text-sm text-gray-500 mt-6 lg:hidden">
            New to Russh Berry? Create an account to start shopping
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
